// The complete plans for a section, expanded and CAPPED on the client off the polled per-line scores -- the
// manufacturing-line twin of the solutions grid's combineSolutionGroups.
//
// The server sends only the STRUCTURE (each part's manufacturing lines + the exact covers over the parts) so the
// unbounded product never rides the wire: a four-part cover of 11 lines per part is 11**4 = 14,641 complete ways,
// a 40 MB payload that crashed the page. Here the page keeps each part's cheapest `topN` -- for a cover of MORE
// THAN TWO parts, where the product explodes -- and expands only those, re-capping as the parallel per-line scores
// land. A line with no score yet sorts LAST (Infinity), so a slow line never holds the listing back -- the bounded
// list shows immediately and refines when its score arrives. A one- or two-part cover keeps every line.

// The stable address of ONE distinct manufacturing line: "<sub_section_index>:<identifier>". The same line is
// reused across many lists, so its best score is filed and read once under this key (see bestScoreByLine). The
// ONE definition -- the component imports it, so the cap and the scoring can never key a line two different ways.
export const lineKey = (line) => `${line.sub_section_index}:${line.identifier}`;

// Every way to pick one item from each array, IN ORDER: [[a, b], [c]] -> [[a, c], [b, c]]. Order matters because a
// list's identifier is its lines' identifiers joined in cover order (Frank::ManufacturingLineList#identifier).
const cartesianProduct = (arrays) => arrays.reduce((accumulator, current) => (
  accumulator.flatMap((combination) => current.map((item) => [...combination, item]))
), [[]]);

export const buildManufacturingLineLists = (subSections, exactCovers, bestScoreByLine, topN) => {
  const scoreOf = (line) => {
    const score = bestScoreByLine[lineKey(line)];
    return typeof score === 'number' ? score : Infinity;
  };
  // Each part's manufacturing lines, cheapest first. Sliced to the cheapest `topN` only for a cover of MORE THAN
  // TWO parts, where the cartesian product (lines ** parts) explodes; a one- or two-part cover keeps every line --
  // the manufacturing-line twin of combineSolutionGroups' per-part cap.
  const rankedLinesOf = (subSection, applyCap) => {
    const ranked = [...((subSection && subSection.manufacturing_lines) || [])].sort((a, b) => scoreOf(a) - scoreOf(b));
    return applyCap ? ranked.slice(0, topN) : ranked;
  };

  const seen = new Set();
  const lists = [];
  (exactCovers || []).forEach((cover) => {
    // Only a cover of more than two parts is capped -- see rankedLinesOf and the sibling combineSolutionGroups.
    const applyCap = cover.length > 2;
    const perPart = cover.map((index) => rankedLinesOf(subSections[index], applyCap));
    cartesianProduct(perPart).forEach((manufacturingLines) => {
      // A cover with an empty part contributes nothing -- correctly: a part that cannot be made cannot be made,
      // however many ways the others can. cartesianProduct already drops it (an empty factor empties the product).
      if (manufacturingLines.length !== cover.length) { return; }
      const identifier = manufacturingLines.map((line) => line.identifier).join('|');
      if (seen.has(identifier)) { return; }
      seen.add(identifier);
      lists.push({ identifier, manufacturing_lines: manufacturingLines });
    });
  });
  return lists;
};

// The listing's complete plans: built from the STRUCTURE the server sends, or -- for a stored/cached response that
// predates the structure shape -- the pre-built lists it still carries.
export const manufacturingLineListsFromListing = (listing, bestScoreByLine, topN) => {
  if (!listing) { return []; }
  if (Array.isArray(listing.sub_sections)) {
    return buildManufacturingLineLists(listing.sub_sections, listing.exact_covers, bestScoreByLine, topN);
  }
  return listing.manufacturing_line_lists || [];
};

// Every manufacturing line the section offers -- the whole candidate set (what the cap ranks), read from the
// structure or, for a legacy/cached payload, flattened out of the pre-built lists. Distinctness is the caller's
// (a line can recur across the pre-built lists); here it is just the flat set.
export const allManufacturingLinesOf = (listing) => {
  if (!listing) { return []; }
  if (Array.isArray(listing.sub_sections)) {
    return listing.sub_sections.flatMap((subSection) => subSection.manufacturing_lines || []);
  }
  return (listing.manufacturing_line_lists || []).flatMap((list) => list.manufacturing_lines || []);
};

// Whether a plan (by its list identifier) is one the section can MAKE -- offered by some exact cover, each of the
// plan's lines belonging to that cover's part. Cap-INDEPENDENT: it reads every part's lines, not the bounded set,
// so a picked or URL-restored plan is judged by whether it can be built, not by whether it currently shows (the
// cap re-shuffles as scores land, and a valid pick must not be dropped just because it fell below the cap).
export const planOfferedByStructure = (listing, identifier) => {
  if (!listing || !identifier) { return false; }
  if (!Array.isArray(listing.sub_sections)) {
    return (listing.manufacturing_line_lists || []).some((list) => list.identifier === identifier);
  }
  const lineIdentifiersByPart = listing.sub_sections.map(
    (subSection) => new Set((subSection.manufacturing_lines || []).map((line) => line.identifier)),
  );
  const wantedIdentifiers = identifier.split('|');
  return (listing.exact_covers || []).some((cover) => (
    cover.length === wantedIdentifiers.length
    && cover.every((partIndex, position) => (
      lineIdentifiersByPart[partIndex] && lineIdentifiersByPart[partIndex].has(wantedIdentifiers[position])
    ))
  ));
};
